import { useState, useRef, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { format } from 'date-fns'
import { toast } from 'sonner'
import {
  ArrowLeft,
  Save,
  Wifi,
  WifiOff,
  Loader2,
  Truck,
  PackageCheck,
  Plus,
  X,
  PackageOpen,
  Paperclip,
  FileCheck,
  ExternalLink,
} from 'lucide-react'
import {
  useDelivery,
  useDeliveryStock,
  useCreateDelivery,
  useUpdateDelivery,
} from '@/hooks/queries/useDeliveries'
import { useDestinations } from '@/hooks/queries/useDestinations'
import { useOnline } from '@/hooks/useOnline'
import { saveDeliveryDraft } from '@/lib/db'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Combobox } from '@/components/ui/combobox'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { DeliveryStockItem } from '@/types'

function buildDefaultDatetime(dateStr: string | null): string {
  const base = dateStr ? new Date(dateStr + 'T12:00:00') : new Date()
  return format(base, "yyyy-MM-dd'T'HH:mm")
}

export function DeliveryFormPage() {
  const navigate = useNavigate()
  const { id: paramId } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isOnline = useOnline()

  const deliveryId = paramId ? parseInt(paramId, 10) : undefined
  const isEditMode = !!deliveryId

  const { data: existingDelivery, isLoading: loadingDelivery } = useDelivery(deliveryId ?? 0)
  const { data: stockItems, isLoading: loadingStock } = useDeliveryStock(
    isEditMode ? { exclude_delivery_id: deliveryId } : undefined
  )
  const { data: destinations, isLoading: loadingDestinations } = useDestinations()
  const createDelivery = useCreateDelivery()
  const updateDelivery = useUpdateDelivery()

  const [destinationId, setDestinationId] = useState<string>('')
  const [deliveredAt, setDeliveredAt] = useState<string>(
    buildDefaultDatetime(searchParams.get('date'))
  )
  const [notes, setNotes] = useState<string>('')
  const [cart, setCart] = useState<DeliveryStockItem[]>([])
  const [attachedFile, setAttachedFile] = useState<File | null>(null)
  const [saving, setSaving] = useState(false)
  const [globalError, setGlobalError] = useState<string>('')
  const [isSaveActionVisible, setIsSaveActionVisible] = useState(false)
  const formSectionRef = useRef<HTMLDivElement | null>(null)
  const saveActionAnchorRef = useRef<HTMLDivElement | null>(null)
  const prefilled = useRef(false)

  // Pre-populate form in edit mode once both delivery and stock are loaded
  useEffect(() => {
    if (!isEditMode || !existingDelivery || !stockItems || prefilled.current) return
    prefilled.current = true

    setDestinationId(String(existingDelivery.destination_id))
    setDeliveredAt(format(new Date(existingDelivery.delivered_at), "yyyy-MM-dd'T'HH:mm"))
    setNotes(existingDelivery.notes ?? '')

    const deliveryItemIds = new Set(existingDelivery.items.map((i) => i.pickup_record_item_id))
    setCart(stockItems.filter((s) => deliveryItemIds.has(s.pickup_record_item_id)))
  }, [isEditMode, existingDelivery, stockItems])

  useEffect(() => {
    const target = saveActionAnchorRef.current
    if (!target) return

    const update = () => {
      const rect = target.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const nearPageBottom = window.scrollY + viewportHeight >= document.documentElement.scrollHeight - 140
      const anchorReached = rect.top <= viewportHeight - 88
      setIsSaveActionVisible(anchorReached || nearPageBottom)
    }

    update()
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [cart.length])

  const handleGoToForm = () => {
    formSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const cartIds = new Set(cart.map((i) => i.pickup_record_item_id))

  const handleAdd = (item: DeliveryStockItem) => {
    if (cartIds.has(item.pickup_record_item_id)) return
    setCart((prev) => [...prev, item])
  }

  const handleRemove = (id: number) => {
    setCart((prev) => prev.filter((i) => i.pickup_record_item_id !== id))
  }

  const validate = (): string[] => {
    const errors: string[] = []
    if (!destinationId) errors.push('Debes seleccionar un destino.')
    if (!deliveredAt) errors.push('Debes ingresar la fecha y hora de entrega.')
    if (cart.length === 0) errors.push('Debes agregar al menos un ítem al carrito.')
    return errors
  }

  const saveDraft = async (payload: {
    destination_id: number
    delivered_at: string
    notes: string
    items: { pickup_record_item_id: number; kilos: number }[]
  }) => {
    await saveDeliveryDraft({
      ...payload,
      document: attachedFile ?? undefined,
      documentName: attachedFile?.name,
      pending_sync: 1,
      updated_at: new Date().toISOString(),
    })
  }

  const handleSubmit = async () => {
    const errors = validate()
    if (errors.length > 0) {
      setGlobalError(errors.join('\n'))
      return
    }

    setGlobalError('')
    setSaving(true)

    const payload = {
      destination_id: parseInt(destinationId),
      delivered_at: deliveredAt.replace('T', ' ') + ':00',
      notes,
      items: cart.map((item) => ({
        pickup_record_item_id: item.pickup_record_item_id,
        kilos: item.available_kilos,
      })),
    }

    try {
      if (isEditMode) {
        await updateDelivery.mutateAsync({ id: deliveryId!, data: payload, document: attachedFile })
        toast.success('Entrega actualizada')
      } else if (isOnline) {
        try {
          await createDelivery.mutateAsync({ data: payload, document: attachedFile })
          toast.success('Entrega registrada')
        } catch (err: unknown) {
          const axiosError = err as { response?: { data?: { message?: string } } }
          if (axiosError?.response) {
            // El servidor respondió (validación, auth, etc.) — propagar el error
            throw err
          }
          // Sin respuesta del servidor (red inestable a pesar de navigator.onLine)
          await saveDraft(payload)
          toast.warning('Sin conexión real — entrega guardada como borrador')
        }
      } else {
        await saveDraft(payload)
        toast.success('Entrega guardada sin conexión')
      }
      navigate('/pickups')
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } }
      setGlobalError(axiosError?.response?.data?.message ?? 'Error al guardar la entrega.')
    } finally {
      setSaving(false)
    }
  }

  const isLoading = loadingStock || loadingDestinations || (isEditMode && loadingDelivery)
  const totalKilos = cart.reduce((sum, item) => sum + Number(item.available_kilos), 0)

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Truck className="w-6 h-6" />
            {isEditMode ? 'Editar entrega' : 'Registrar entrega'}
          </h1>
        </div>
        {!isEditMode && (
          <div
            className={cn(
              'flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium',
              isOnline ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
            )}
          >
            {isOnline ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {isOnline ? 'En línea' : 'Sin conexión'}
          </div>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-sm">Ítems disponibles en camión</CardTitle>
              <CardDescription>
                Toca el botón <span className="font-medium">+</span> para agregar un ítem a la entrega.
                Los ítems agregados se marcan en verde.
              </CardDescription>
            </div>
            {!isLoading && (stockItems ?? []).length > 0 && cart.length < (stockItems ?? []).length && (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 border-[#b9d94a] text-[#7c8c43] hover:bg-[#dff24b]/20"
                onClick={() => setCart(stockItems ?? [])}
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                Agregar todos
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
            {/* Left — stock list */}
            <div className="space-y-2">
              {isLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Cargando stock…
                </div>
              ) : (stockItems ?? []).length === 0 ? (
                <div className="flex flex-col items-center py-8 text-center">
                  <PackageOpen className="mb-2 h-8 w-8 text-gray-300" />
                  <p className="text-sm text-gray-400">No hay stock disponible en el camión.</p>
                </div>
              ) : (
                (stockItems ?? []).map((item) => {
                  const isAdded = cartIds.has(item.pickup_record_item_id)
                  return (
                    <div
                      key={item.pickup_record_item_id}
                      className={cn(
                        'flex items-center justify-between rounded-xl border px-3 py-3 transition-colors',
                        isAdded
                          ? 'border-green-200 bg-green-50'
                          : 'border-slate-200 bg-slate-50 hover:border-[#b9d94a] hover:bg-[#f4f8d4]'
                      )}
                    >
                      <div className="min-w-0 flex-1 pr-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate text-sm font-medium text-slate-900">
                            {item.waste_type.name}
                          </p>
                          {isAdded && (
                            <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700">
                              Agregado
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Disponible: <strong>{item.available_kilos} kg</strong>
                          {item.client && ` · ${item.client.trade_name}`}
                        </p>
                      </div>
                      {isAdded ? (
                        <button
                          type="button"
                          onClick={() => handleRemove(item.pickup_record_item_id)}
                          className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition-colors hover:border-red-300 hover:text-red-600"
                          aria-label="Quitar de la entrega"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleAdd(item)}
                          className="flex h-9 w-9 items-center justify-center rounded-full border border-[#383f2d] bg-[#383f2d] text-[#dff24b] transition-colors hover:bg-[#4a5236]"
                          aria-label="Agregar a la entrega"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )
                })
              )}
            </div>

            {/* Right — sticky summary + form */}
            <div className="space-y-4 lg:sticky lg:top-4">
              {/* Form fields + cart detail */}
              <div ref={formSectionRef} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                      <PackageCheck className="h-4 w-4 text-[#7c8c43]" />
                      Resumen de entrega
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {cart.length === 0
                        ? 'Sin ítems seleccionados'
                        : `${cart.length} ítem${cart.length !== 1 ? 's' : ''} · ${totalKilos} kg total`}
                    </p>
                  </div>
                </div>

                {cart.length > 0 && (
                  <div className="space-y-1.5">
                    {cart.map((item) => (
                      <div
                        key={item.pickup_record_item_id}
                        className="flex items-center justify-between rounded-lg border border-[#b9d94a]/50 bg-[#dff24b]/10 px-3 py-2"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{item.waste_type.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.available_kilos} kg
                            {item.client && ` · ${item.client.trade_name}`}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 shrink-0 text-muted-foreground hover:text-red-600"
                          onClick={() => handleRemove(item.pickup_record_item_id)}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label>Destino *</Label>
                  <Combobox
                    options={(destinations ?? []).map((d) => ({
                      value: String(d.id),
                      label: `${d.name}${d.is_collection_point ? ' (Acopio)' : ''}${d.is_valorizer ? ' (Valorizador)' : ''}`,
                    }))}
                    value={destinationId || null}
                    onChange={setDestinationId}
                    placeholder={loadingDestinations ? 'Cargando destinos…' : 'Selecciona un destino'}
                    searchPlaceholder="Buscar destino…"
                    emptyMessage="Sin resultados."
                    disabled={loadingDestinations}
                    contentClassName="w-full"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="delivered_at">Fecha y hora de entrega *</Label>
                  <Input
                    id="delivered_at"
                    type="datetime-local"
                    value={deliveredAt}
                    onChange={(e) => setDeliveredAt(e.target.value)}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="notes">Observaciones</Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Opcional"
                    rows={2}
                  />
                </div>

                {/* Documento de respaldo */}
                <div className="space-y-1.5">
                  <Label htmlFor="document">Documento de respaldo</Label>

                  {/* Documento existente en modo edición */}
                  {isEditMode && existingDelivery?.document && !attachedFile && (
                    <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                      <FileCheck className="h-4 w-4 shrink-0 text-green-600" />
                      <span className="min-w-0 flex-1 truncate text-slate-700">
                        {existingDelivery.document.name}
                      </span>
                      {existingDelivery.document.url && (
                        <a
                          href={existingDelivery.document.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shrink-0 text-blue-600 hover:text-blue-800"
                          aria-label="Ver documento"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                  )}

                  {/* Archivo nuevo seleccionado */}
                  {attachedFile ? (
                    <div className="flex items-center gap-2 rounded-lg border border-[#b9d94a]/60 bg-[#dff24b]/10 px-3 py-2 text-sm">
                      <Paperclip className="h-4 w-4 shrink-0 text-[#7c8c43]" />
                      <span className="min-w-0 flex-1 truncate text-slate-700">{attachedFile.name}</span>
                      <button
                        type="button"
                        onClick={() => setAttachedFile(null)}
                        className="shrink-0 text-slate-400 hover:text-red-600"
                        aria-label="Quitar adjunto"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <label
                      htmlFor="document"
                      className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:border-[#b9d94a] hover:bg-[#f4f8d4]"
                    >
                      <Paperclip className="h-4 w-4 shrink-0" />
                      <span>
                        {isEditMode && existingDelivery?.document
                          ? 'Reemplazar documento (PDF, JPG, PNG · máx. 10 MB)'
                          : 'Adjuntar documento (PDF, JPG, PNG · máx. 10 MB)'}
                      </span>
                    </label>
                  )}

                  <input
                    id="document"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="sr-only"
                    onChange={(e) => setAttachedFile(e.target.files?.[0] ?? null)}
                  />
                </div>

                {globalError && (
                  <div className="rounded-md border border-red-300 bg-red-50 p-3">
                    {globalError.split('\n').map((line, i) => (
                      <p key={i} className="text-sm text-red-700">{line}</p>
                    ))}
                  </div>
                )}

                <div ref={saveActionAnchorRef} className="h-px" aria-hidden="true" />

                <div className="hidden lg:flex">
                  <Button
                    onClick={handleSubmit}
                    disabled={saving || isLoading || cart.length === 0}
                    className="w-full h-11 text-sm font-semibold shadow-lg"
                  >
                    {saving ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Guardando…</>
                    ) : (
                      <><Save className="w-4 h-4 mr-2" /> {isEditMode ? 'Guardar cambios' : isOnline ? 'Registrar entrega' : 'Guardar sin conexión'}</>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="sticky bottom-4 flex items-center gap-3 lg:hidden">
        <div className="flex-1">
          {isSaveActionVisible ? (
            <Button
              className="h-12 w-full text-base font-semibold shadow-lg"
              disabled={saving || isLoading || cart.length === 0}
              onClick={handleSubmit}
            >
              {saving ? (
                <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Guardando…</>
              ) : (
                <><Save className="mr-2 h-5 w-5" /> {isEditMode ? 'Guardar cambios' : isOnline ? 'Registrar entrega' : 'Guardar sin conexión'}</>
              )}
            </Button>
          ) : cart.length > 0 ? (
            <Button
              className="h-12 w-full text-base font-semibold shadow-lg"
              onClick={handleGoToForm}
            >
              Ir al formulario de entrega
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
